import { IToolContext, IToolManifest } from '../../../core/tools/interfaces/ITool';
import { DeleteTool } from '../../../core/tools/standard/DeleteTool';
import { Node, Edge } from '../../../core/models/scene';
import { DeleteFrameCascadeCommand } from '../../../core/commands/DeleteFrameCascadeCommand';
import { DeleteNodeCommand } from '../../../core/commands/DeleteNodeCommand';
import { DeleteEdgeCommand } from '../../../core/commands/edges/DeleteEdgeCommand';
import { CompositeCommand, ICommand } from '../../../core/commands/ICommand';

// Genogram-specific delete tool
export class GenogramDeleteTool extends DeleteTool {
  manifest: IToolManifest;
  
  private nodes: Node[] = [];
  private edges: Edge[] = [];
  private addNode: ((node: Node) => void) | null = null;
  private addEdge: ((edge: Edge) => void) | null = null;
  private frameManager: any = null;
  
  constructor() {
    super();
    
    // Build manifest from base
    this.manifest = {
      ...this.getBaseManifest(),
      id: 'genogram-delete',
      supportedDiagramTypes: ['genogram'],
    } as IToolManifest;
  }
  
  canExecute(context: any): boolean {
    if (!context?.canWrite || context?.diagramType !== 'genogram') return false;
    // Allow execution if there is an active frame, or at least one deletable (unlocked) item
    const hasActiveFrame = !!context?.frame;
    if (hasActiveFrame) return true;
    try {
      // Evaluate against the passed context: this runs during keyboard-shortcut
      // activation, before onActivate has populated this.context.
      return this.getItemsToDelete(context).length > 0;
    } catch {
      return false;
    }
  }

  setData(
    nodes: Node[],
    edges: Edge[],
    addNode: (node: Node) => void,
    _deleteNode: (id: string) => void,
    addEdge: (edge: Edge) => void,
    _deleteEdge: (id: string) => void
  ): void {
    this.nodes = nodes;
    this.edges = edges;
    this.addNode = addNode;
    this.addEdge = addEdge;
  }

  setFrameAndCommandManagers(frameManager: any): void {
    this.frameManager = frameManager;
  }
  
  getItemsToDelete(context: IToolContext | null = this.context): any[] {
    if (!context) return [];

    const selectedIds = Array.from(context.selection);
    if (selectedIds.length === 0) return [];
    
    // Find selected nodes (exclude locked)
    const nodesToDelete = this.nodes.filter(n => selectedIds.includes(n.id) && !(n.locked === true || (n as any)?.attrs?.locked === true));
    const nodeIdsToDelete = new Set(nodesToDelete.map(n => n.id));
    
    // Find edges to delete (selected edges + edges connected to deleted nodes)
    const edgesToDelete = this.edges.filter(e => 
      selectedIds.includes(e.id) ||
      nodeIdsToDelete.has(e.from) ||
      nodeIdsToDelete.has(e.to)
    );
    
    // Return combined list of items to delete
    return [...nodesToDelete, ...edgesToDelete];
  }
  
  deleteItems(_ids: string[]): void {
    // This method is called by the base DeleteItemsCommand
    // The actual deletion should be handled by the command's execute method
  }

  execute(): ICommand | null {
    
    // If a frame is active in context, return a cascade delete command instead of base behavior
    if (this.frameManager && this.context?.frame) {
      const frameId = this.context.frame.id;
      return new DeleteFrameCascadeCommand(this.frameManager, frameId);
    }
    
    // Get items to delete
    const itemsToDelete = this.getItemsToDelete();
    if (itemsToDelete.length === 0) {
      return null;
    }
    
    // Separate nodes and edges
    const nodeIds = itemsToDelete.filter(item => this.nodes.some(n => n.id === item.id)).map(item => item.id);
    const edgeIds = itemsToDelete.filter(item => this.edges.some(e => e.id === item.id)).map(item => item.id);
    
    
    // Create a composite command for the deletion
    const commands: ICommand[] = [];
    
    // Add node deletion command
    if (nodeIds.length > 0) {
      // Use context's command factory if available (enables comment restoration), otherwise create directly
      const deleteCommand = this.context?.createDeleteNodeCommand
        ? this.context.createDeleteNodeCommand(nodeIds)
        : new DeleteNodeCommand(nodeIds);
      commands.push(deleteCommand);
    }
    
    // Add standalone edge deletion commands
    const standaloneEdgeIds = edgeIds.filter(edgeId => {
      const edge = this.edges.find(e => e.id === edgeId);
      return edge && !nodeIds.includes(edge.from) && !nodeIds.includes(edge.to);
    });
    
    standaloneEdgeIds.forEach(id => {
      commands.push(new DeleteEdgeCommand(id));
    });
    
    // Return composite command if multiple commands, otherwise return single command
    if (commands.length === 0) {
      return null;
    } else if (commands.length === 1) {
      return commands[0];
    } else {
      // Create a composite command for multiple deletions
      return new CompositeCommand('Delete Items', commands);
    }
  }
  
  restoreItems(items: any[]): void {
    // Separate nodes and edges
    const nodes = items.filter(item => 'type' in item && item.type === 'person');
    const edges = items.filter(item => 'from' in item && 'to' in item);
    
    // Restore nodes first, then edges
    nodes.forEach(node => this.addNode?.(node));
    edges.forEach(edge => this.addEdge?.(edge));
  }
}
